import React, { useEffect } from "react";

/**
 * The small confirm card the archive package's two dialogs share — the design
 * handoff's "Reactivate confirm": a 420px card, an 18px bold title, one body
 * line, and Cancel plus a single blue primary, right-aligned.
 *
 * Deliberately its own tiny shell rather than a third ConfirmationModal
 * variant: that component's two variants are both wider (max-w-xl / 520px)
 * and centre their buttons, and the handoff pins this card's width and the
 * right-aligned pair. Extending the shared modal would have meant a layout
 * fork inside it for exactly two call sites.
 */
const RequestConfirmDialog = ({
  isOpen,
  title,
  body,
  confirmLabel,
  onClose,
  onConfirm,
  loading = false,
}) => {
  // Escape closes, like the backdrop — but not while the action runs, so a
  // stray key cannot dismiss a confirm that is already in flight.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  return (
    /* Backdrop click closes (handoff behaviour). stopPropagation on BOTH
       layers: the dialog can be mounted inside a clickable list row, and
       neither a backdrop click nor a click in the card may toggle that row. */
    <div
      onClick={(event) => {
        event.stopPropagation();
        if (!loading) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="flex w-full max-w-[420px] flex-col gap-3.5 rounded-2xl bg-white p-6"
      >
        <h3 className="text-lg font-bold text-[#0B0B0B]">{title}</h3>
        <p className="text-[15px] leading-relaxed text-[#475569]">{body}</p>
        <div className="mt-1.5 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={`min-h-[44px] rounded-[10px] border border-[#D5DBE3] bg-white px-[18px] text-[15px] font-semibold text-[#0B0B0B] ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`min-h-[44px] rounded-[10px] bg-[#2283FF] px-5 text-[15px] font-semibold text-white ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"
            }`}
          >
            {loading ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestConfirmDialog;
